import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { CbLoadingSpinnerModule } from 'cb-hub-lib';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';

export type PdfViewerSource = string | Blob | null;

@Component({
    selector: 'cb-pdf-viewer',
    standalone: true,
    imports: [
        CommonModule,
        NgxExtendedPdfViewerModule,
        CbLoadingSpinnerModule,
        MatIcon,
        MatIconButton
    ],
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfViewerComponent implements OnChanges, OnDestroy {
    @Input() public src: PdfViewerSource = null;
    @Input() public documentName: string | null = null;
    @Input() public emptyMessage = 'No document selected';
    @Input() public showHeader = true;
    @Input() public showCloseButton = true;
    @Input() public loading = false;

    @Output() public closed = new EventEmitter<void>();

    public resolvedSrc: string | null = null;
    public viewerLoading = false;
    public hasLoadingError = false;

    private objectUrlToRevoke: string | null = null;

    public get displayDocumentName(): string {
        return this.documentName || 'Document';
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['src']) {
            this.applySource(this.src);
        }
    }

    public close(): void {
        this.resetViewerState();
        this.closed.emit();
    }

    public onPdfLoaded(): void {
        this.viewerLoading = false;
        this.hasLoadingError = false;
    }

    public onPdfLoadingFailed(): void {
        this.viewerLoading = false;
        this.hasLoadingError = true;
    }

    private applySource(input: PdfViewerSource): void {
        this.revokeObjectUrl();
        this.hasLoadingError = false;

        if (!input) {
            this.resetViewerState();
            return;
        }

        this.viewerLoading = true;

        if (typeof input === 'string') {
            this.resolvedSrc = input;
            return;
        }

        this.objectUrlToRevoke = URL.createObjectURL(input);
        this.resolvedSrc = this.objectUrlToRevoke;
    }

    private resetViewerState(): void {
        this.revokeObjectUrl();
        this.resolvedSrc = null;
        this.viewerLoading = false;
        this.hasLoadingError = false;
    }

    private revokeObjectUrl(): void {
        if (!this.objectUrlToRevoke) {
            return;
        }

        URL.revokeObjectURL(this.objectUrlToRevoke);
        this.objectUrlToRevoke = null;
    }

    public ngOnDestroy(): void {
        this.revokeObjectUrl();
    }
}
